import {stickerTypes} from "../components/form/StickerGeneratorForm";

/**
 * 스티커 선택 어휘 — 가입 위저드 (4) 와 관리자 `init-setting` 이 **같은 출처**를 쓴다(2026-10-12).
 *
 * ## 어휘
 *
 * `sticker_type` 값 = `<방향>_<색>`. 예: `hor_blue`(가로형 파란색), `ver_blue`(세로형 파란색).
 *
 * 🔑 **2026-10-12 부터 `ver_*` 는 POP 이 아니라 세로형 스티커다.** 가입 위저드가 POP 을 선택지에서 없앴고
 *    스티커를 가로형(150×100mm) / 세로형(100×150mm) 으로 받는다. 서버 `StickerSelection` KDoc 참고 —
 *    `pop_type` 칸의 `ver_*` 만 옛 POP 이다(단종, 읽기 전용).
 *
 * 이미지·한글 색 이름은 `stickerTypes`(`StickerGeneratorForm.js`)가 소유한다. 여기서는 그 목록을
 * **방향 × 색** 으로 해석만 한다 — 색이 추가되면 `stickerTypes` 에 두 방향을 넣으면 자동으로 늘어난다.
 */

export const STICKER_ORIENTATIONS = [
    {id: 'hor', label: '가로형', spec: '150 × 100mm'},
    {id: 'ver', label: '세로형', spec: '100 × 150mm'},
];

/** `stickerTypes` 의 id 에서 색을 뽑는다. `hor_blue` → `blue`. 모르는 형식은 null. */
const parse = (id) => {
    const m = /^(hor|ver)_([a-z]+)$/.exec(String(id ?? ''));
    return m ? {orientation: m[1], color: m[2]} : null;
};

/** 한글 이름 '가로형 파란색' → '파란색'. 이름이 그 형식이 아니면 색 id 를 그대로 쓴다. */
const colorLabelOf = (type, color) =>
    String(type?.name ?? '').replace(/^(가로형|세로형)\s*/, '').trim() || color;

/**
 * 색 목록. `stickerTypes` 에서 도출하며 **두 방향에 모두 있는 색만** 고객에게 보인다 —
 * 한쪽 방향 이미지만 있는 색을 내보내면 그 방향을 고른 고객이 빈 미리보기를 본다.
 */
export const STICKER_COLORS = (() => {
    const byColor = new Map();
    stickerTypes.forEach((type) => {
        const parsed = parse(type.id);
        if (!parsed) return;
        const entry = byColor.get(parsed.color) ?? {id: parsed.color, label: colorLabelOf(type, parsed.color), images: {}};
        entry.images[parsed.orientation] = type.imageUrl;
        byColor.set(parsed.color, entry);
    });
    return Array.from(byColor.values()).filter((c) => c.images.hor && c.images.ver);
})();

export const STICKER_FULFILLMENT = {
    /** QR·스티커 이미지만 무상 제공. 발송 없음, 수량 없음. */
    IMAGE_ONLY: 'IMAGE_ONLY',
    /** 제작해서 매장으로 택배 발송. */
    PRINT: 'PRINT',
};

export const stickerTypeId = (orientation, color) =>
    orientation && color ? `${orientation}_${color}` : '';

/** `hor_blue` → `{orientation: 'hor', color: 'blue'}`. 모르는 값(옛 'none' 포함)은 null. */
export const parseStickerType = parse;

export const stickerImageUrl = (orientation, color) =>
    STICKER_COLORS.find((c) => c.id === color)?.images?.[orientation] ?? null;

/** `hor_blue` → '가로형 파란색'. 모르는 값은 그대로 돌려준다(옛 데이터 표기용). */
export const describeStickerType = (id) => {
    const parsed = parse(id);
    if (!parsed) return id ?? '';
    const orientation = STICKER_ORIENTATIONS.find((o) => o.id === parsed.orientation)?.label ?? parsed.orientation;
    const color = STICKER_COLORS.find((c) => c.id === parsed.color)?.label ?? parsed.color;
    return `${orientation} ${color}`;
};

/**
 * 수량 옵션 — '이미지만(무료)' / '기본 20장' / '추가 수량'.
 *
 * 금액은 여기 두지 않는다. 서버 `InstallPricing` 이 소유하고 `GET /account/install-request/quote` 의
 * `pricing` 으로 받는다. 화면 문구에 숫자를 박으면 가격이 바뀔 때 청구와 어긋난다.
 */
export const QUANTITY_OPTIONS = {
    IMAGE_ONLY: 'image',
    BASE: 'base',
    EXTRA: 'extra',
};

/**
 * 추가 수량 정규화: 기본 수량 + 단위의 배수로 **올림**. 기본 수량 이하면 다음 단위(기본+단위)로.
 * 내림하면 고객이 요청한 것보다 적게 보낸다. 서버도 같은 규칙으로 한 번 더 눕힌다.
 */
export const normalizeExtraQuantity = (raw, pricing) => {
    const base = pricing?.stickerBaseQuantity ?? 20;
    const step = pricing?.stickerStepQuantity ?? 10;
    const parsed = Number.parseInt(String(raw ?? '').replace(/\D/g, ''), 10);
    if (!Number.isFinite(parsed) || parsed <= base) return base + step;
    return base + Math.ceil((parsed - base) / step) * step;
};

/** 수량(정규화된 값)의 스티커 공급가. 서버 `InstallPricing.stickerNet` 과 같은 식. 0장이면 0. */
export const stickerNetOf = (quantity, pricing) => {
    if (!pricing || !quantity || quantity <= 0) return 0;
    const steps = Math.max(0, Math.floor((quantity - pricing.stickerBaseQuantity) / pricing.stickerStepQuantity));
    return pricing.stickerBaseNet + steps * pricing.stickerStepNet;
};
